import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import { divisionLabel } from '../../../constants/divisions';
import { formatDate } from '../../../utils/formatDate';
import { listDirectiveHistory } from '../../../services/projectDirectivesService';

/**
 * Historique d'une directive, partage par la modale Historique et la fiche.
 * Lecture seule de entity_change_log, alimente par le trigger de la base.
 */

// Charge l'historique ; `reloadKey` relance la lecture (ex. apres une modification).
export const useDirectiveHistory = (directiveId, reloadKey) => {
  const [state, setState] = useState({ loading: true, error: false, rows: [], names: {} });

  useEffect(() => {
    if (!directiveId) return undefined;
    let alive = true;
    setState((prev) => ({ ...prev, loading: true, error: false }));
    listDirectiveHistory(directiveId)
      ?.then((res) => { if (alive) setState({ loading: false, error: false, rows: res?.rows || [], names: res?.names || {} }); })
      ?.catch(() => { if (alive) setState({ loading: false, error: true, rows: [], names: {} }); });
    return () => { alive = false; };
  }, [directiveId, reloadKey]);

  return state;
};

// Horodatage complet : date de l'app + heure locale.
const formatDateTime = (value, language) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d?.getTime())) return '—';
  const time = d?.toLocaleTimeString(language === 'fr' ? 'fr-CA' : 'en-CA', { hour: '2-digit', minute: '2-digit' });
  return `${formatDate(value, language)} · ${time}`;
};

// `limit` : n'affiche que les N changements les plus recents.
const DirectiveHistoryList = ({ history, limit }) => {
  const { t, language } = useTranslation();
  const rows = limit ? history?.rows?.slice(0, limit) : history?.rows;

  // Valeur lisible selon le champ ; une valeur vide s'affiche « — ».
  const display = (field, value) => {
    if (value == null || value === '') return '—';
    switch (field) {
      case 'outcome': return t(`extras.directives.outcome.${value}`);
      case 'division': return divisionLabel(t, value);
      case 'date_directive': return formatDate(String(value)?.slice(0, 10), language);
      case 'professional_id':
      case 'rfi_id':
      case 'extra_id':
        return history?.names?.[field]?.[value] || t('extras.directives.history.unknownRef');
      default: return String(value);
    }
  };

  if (history?.loading) {
    return (
      <div className="flex justify-center py-6">
        <Icon name="Loader2" size={20} className="animate-spin text-primary" />
      </div>
    );
  }
  if (history?.error) {
    return (
      <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        {t('extras.directives.history.error')}
      </p>
    );
  }
  if (!rows?.length) {
    return <p className="text-xs text-gray-400 text-center py-4">{t('extras.directives.history.empty')}</p>;
  }

  return (
    <ul className="divide-y divide-gray-100">
      {rows?.map((r) => (
        <li key={r?.id} className="py-2.5">
          <p className="text-[10px] text-gray-400">
            {formatDateTime(r?.changed_at, language)} · {r?.changedByName || t('extras.directives.history.system')}
          </p>
          <p className="text-xs text-gray-800 mt-0.5">
            <span className="font-medium">{t(`extras.directives.history.fields.${r?.field}`)}</span>
            {r?.op === 'insert' && (
              <span className="text-gray-400"> · {t('extras.directives.history.created')}</span>
            )}
          </p>
          <p className="text-xs text-gray-600 mt-0.5 break-words">
            <span className="text-gray-400 line-through decoration-gray-300">{display(r?.field, r?.old_value)}</span>
            <span className="mx-1.5 text-gray-400">→</span>
            <span className="text-gray-900">{display(r?.field, r?.new_value)}</span>
          </p>
        </li>
      ))}
    </ul>
  );
};

export default DirectiveHistoryList;
